import { useEffect } from 'react';
import aa from 'search-insights';
import {
  clearAlgoliaAttributedClick,
  getAlgoliaAttributedClick,
  isValidAlgoliaAttributedClick,
} from './algoliaAttributedClick';

export const TRACKED_EVENTS_KEY = 'trackedEvents';
export const TRACKED_EVENTS_CROSS_TAB_KEY = 'trackedEventsCrossTab';
export const TRACKED_EVENTS_TTL_MS = 30 * 60 * 1000;
export const TRACKED_EVENTS_CROSS_TAB_MAX_KEYS = 200;

type TrackedEvents = Record<string, boolean>;
type TrackedEventTimestamps = Record<string, number>;

type AlgoliaInsightsTrackerProps = {
  config: {
    apiKey: string;
    appId: string;
    indexName: string;
  };
  objectID: string;
  userToken: string;
};

export const buildTrackedEventKey = (
  eventName: string,
  objectID: string,
  algoliaQueryID: string,
): string => JSON.stringify([algoliaQueryID, objectID, eventName]);

const AlgoliaInsightsTracker = ({
  config: { apiKey, appId, indexName },
  objectID,
  userToken,
}: AlgoliaInsightsTrackerProps) => {
  const getSessionTrackedEvents = (): TrackedEvents => {
    try {
      return JSON.parse(sessionStorage.getItem(TRACKED_EVENTS_KEY) || '{}');
    } catch {
      return {};
    }
  };

  const setSessionTrackedEvents = (events: TrackedEvents): void =>
    sessionStorage.setItem(TRACKED_EVENTS_KEY, JSON.stringify(events));

  const setCrossTabTrackedEvents = (events: TrackedEventTimestamps): void => {
    try {
      localStorage.setItem(
        TRACKED_EVENTS_CROSS_TAB_KEY,
        JSON.stringify(events),
      );
    } catch {
      // Ignore localStorage write failures; session-scoped dedupe still applies.
    }
  };

  const capCrossTabTrackedEvents = (
    events: TrackedEventTimestamps,
  ): TrackedEventTimestamps => {
    const entries = Object.entries(events);
    if (entries.length <= TRACKED_EVENTS_CROSS_TAB_MAX_KEYS) return events;

    // Keep newest entries when the map exceeds max capacity.
    entries.sort((a, b) => b[1] - a[1]);
    return Object.fromEntries(
      entries.slice(0, TRACKED_EVENTS_CROSS_TAB_MAX_KEYS),
    ) as TrackedEventTimestamps;
  };

  const getCrossTabTrackedEvents = (): TrackedEventTimestamps => {
    try {
      const trackedEvents = JSON.parse(
        localStorage.getItem(TRACKED_EVENTS_CROSS_TAB_KEY) || '{}',
      ) as TrackedEventTimestamps;

      const now = Date.now();
      const validEvents: TrackedEventTimestamps = {};

      Object.entries(trackedEvents).forEach(([eventKey, timestamp]) => {
        if (now - timestamp <= TRACKED_EVENTS_TTL_MS) {
          validEvents[eventKey] = timestamp;
        }
      });

      const cappedEvents = capCrossTabTrackedEvents(validEvents);

      if (
        Object.keys(cappedEvents).length !== Object.keys(trackedEvents).length
      ) {
        setCrossTabTrackedEvents(cappedEvents);
      }

      return cappedEvents;
    } catch {
      return {};
    }
  };

  const getTrackedEvents = (): TrackedEvents => {
    const sessionEvents = getSessionTrackedEvents();
    const crossTabEvents = getCrossTabTrackedEvents();

    return Object.keys(crossTabEvents).reduce(
      (acc, eventKey) => {
        acc[eventKey] = true;
        return acc;
      },
      { ...sessionEvents },
    );
  };

  const markTrackedEvent = (eventKey: string): void => {
    const sessionEvents = getSessionTrackedEvents();
    sessionEvents[eventKey] = true;
    setSessionTrackedEvents(sessionEvents);

    const crossTabEvents = getCrossTabTrackedEvents();
    crossTabEvents[eventKey] = Date.now();
    setCrossTabTrackedEvents(crossTabEvents);
  };

  const trackingKey = (
    eventName: string,
    objectID: string,
    algoliaQueryID: string,
  ): string =>
    // Dedupe is scoped by query + object + event so identical event names across
    // different vehicles (or searches) still count as separate conversions.
    buildTrackedEventKey(eventName, objectID, algoliaQueryID);

  const sendConversion = (
    eventName: string,
    indexName: string,
    objectID: string,
    algoliaQueryID: string,
    userToken: string,
  ): boolean => {
    if (!eventName || !indexName || !objectID || !algoliaQueryID || !userToken)
      return false;

    aa('convertedObjectIDsAfterSearch', {
      eventName,
      index: indexName,
      userToken,
      objectIDs: [objectID],
      queryID: algoliaQueryID,
    });

    return true;
  };

  const trackConversionOnce = (
    eventName: string,
    indexName: string,
    objectID: string,
    algoliaQueryID: string,
    userToken: string,
  ) => {
    const trackedEvents = getTrackedEvents();
    const eventKey = trackingKey(eventName, objectID, algoliaQueryID);

    if (!trackedEvents[eventKey]) {
      const wasSent = sendConversion(
        eventName,
        indexName,
        objectID,
        algoliaQueryID,
        userToken,
      );

      if (wasSent) {
        markTrackedEvent(eventKey);
      }
    }
  };

  useEffect(() => {
    const attributedClick = getAlgoliaAttributedClick();

    if (!isValidAlgoliaAttributedClick(attributedClick, objectID)) {
      if (attributedClick) {
        // Remove stale/mismatched attribution payloads to avoid reusing bad data.
        clearAlgoliaAttributedClick();
      }
      return;
    }

    const { queryID: algoliaQueryID } = attributedClick;

    aa('init', { appId, apiKey, useCookie: false });
    aa('setUserToken', userToken);

    const syncCrossTabTrackedEvents = () => {
      const crossTabEvents = getCrossTabTrackedEvents();
      const sessionEvents = getSessionTrackedEvents();
      const mergedEvents: TrackedEvents = { ...sessionEvents };

      Object.keys(crossTabEvents).forEach((eventKey) => {
        mergedEvents[eventKey] = true;
      });

      setSessionTrackedEvents(mergedEvents);
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== TRACKED_EVENTS_CROSS_TAB_KEY) return;
      syncCrossTabTrackedEvents();
    };

    syncCrossTabTrackedEvents();

    const handleClick = (e: MouseEvent) => {
      // Delegate from body so tracking still works for dynamically rendered CTAs.
      const el = (e.target as HTMLElement)?.closest<HTMLElement>(
        '[data-insight-event]',
      );
      if (!el || !el.dataset.insightEvent || !el.dataset.eventName) return;
      if (el.dataset.insightEvent !== 'conversion') return;

      const { eventName } = el.dataset;

      if (objectID && indexName && algoliaQueryID && eventName) {
        trackConversionOnce(
          eventName,
          indexName,
          objectID,
          algoliaQueryID,
          userToken,
        );
      }
    };

    document.body.addEventListener('click', handleClick);
    window.addEventListener('storage', handleStorage);

    return () => {
      document.body.removeEventListener('click', handleClick);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  return null;
};

export default AlgoliaInsightsTracker;
